import * as NextSkyTheme from "@NextSkyTheme/global";

if (!customElements.get("content-effect")) {
  customElements.define(
    "content-effect",
    class ContentEffect extends HTMLElement {
      constructor() {
        super();
        this.infoElements = this.querySelectorAll(".block__information_text");
        this.initialized = false;
        this.isVisible = false;
        this.safetyTimer = null;
        this.revealTimer = null;
        this.isFeaturedBanner = this.hasAttribute("data-featured-banner-scroll");
        this.mediaQuery = null;
        this.handleMediaQueryChange = null;
        this.mobileCarouselReady = false;
        this.scrollEl = null;
        this.dots = null;
        this.onMobileScroll = null;
        this.onMobileResize = null;
        this.dotHandlers = null;
      }

      connectedCallback() {
        this.infoElements = this.querySelectorAll(".block__information_text");
        this.isFeaturedBanner =
          this.hasAttribute("data-featured-banner-scroll") ||
          this.closest(".section-featured-product-banner") !== null;
        this.mediaQuery = window.matchMedia("(max-width: 1024.98px)");
        const syncViewport = (mediaQuery) => {
          if (mediaQuery.matches) {
            this.initializeMobile();
          } else {
            // Re-arm desktop from-top animation when returning from mobile.
            if (this.isFeaturedBanner) {
              this.initialized = false;
              this.isVisible = false;
              if (this.observer) {
                this.observer.disconnect();
                this.observer = null;
              }
            }
            if (document.readyState === "loading") {
              document.addEventListener("DOMContentLoaded", () => this.initialize());
            } else {
              this.initialize();
            }
          }
        };
        this.handleMediaQueryChange = this.safeThrottle(syncViewport, 100);

        syncViewport(this.mediaQuery);
        this.mediaQuery.addEventListener("change", this.handleMediaQueryChange);
      }

      safeThrottle(fn, delay) {
        if (NextSkyTheme && typeof NextSkyTheme.throttle === "function") {
          return NextSkyTheme.throttle(fn, delay);
        }
        return fn;
      }

      initialize() {
        if (this.initialized) return;
        this.infoElements = this.querySelectorAll(".block__information_text");
        if (this.infoElements.length === 0) {
          this.classList.add("is-ready");
          this.initialized = true;
          return;
        }

        if (this.isFeaturedBanner) {
          this.teardownMobileCarousel();
          this.teardownMobileHotspots();
          this.setupFeaturedBanner();
          this.initialized = true;
          return;
        }

        this.setupInitialStates();
        this.setupScrollObserver();
        this.initialized = true;
      }

      initializeMobile() {
        if (this.safetyTimer) {
          window.clearTimeout(this.safetyTimer);
          this.safetyTimer = null;
        }
        if (this.revealTimer) {
          window.clearTimeout(this.revealTimer);
          this.revealTimer = null;
        }
        if (this.observer) {
          this.observer.disconnect();
          this.observer = null;
        }
        this.infoElements = this.querySelectorAll(".block__information_text");
        this.removeAttribute("data-banner-pending");
        this.classList.remove("is-animating");
        this.classList.add("is-ready");
        this.initialized = true;
        this.isVisible = true;
        if (this.infoElements.length === 0) return;
        this.infoElements.forEach((element) => {
          element.style.opacity = "1";
          element.style.transform = "";
          element.style.willChange = "auto";
          const card = element.querySelector(
            ".featured-banner-card, .block_infor-text",
          );
          if (card) {
            card.style.opacity = "1";
            card.style.visibility = "visible";
            card.style.transform = "";
          }
        });
        if (this.isFeaturedBanner) {
          if (this.setupMobileHotspots()) {
            this.teardownMobileCarousel();
          } else {
            this.setupMobileCarousel();
          }
        } else {
          this.teardownMobileCarousel();
        }
      }

      setupMobileHotspots() {
        const section = this.closest(".section-featured-product-banner");
        const pins = section?.querySelectorAll("[data-pin-index]");
        if (!section || !pins?.length) return false;

        this.teardownMobileHotspots();
        this.hotspotPins = pins;
        this.hotspotCards = this.infoElements;
        this.hotspotHandlers = [];

        const setActive = (index) => {
          if (this.hotspotIndex === index && this.mobileHotspotsReady) return;
          this.hotspotIndex = index;
          this.hotspotPins.forEach((pin, i) => {
            const active = i === index;
            pin.classList.toggle("is-active", active);
            pin.setAttribute("aria-pressed", active ? "true" : "false");
          });
          this.hotspotCards.forEach((card, i) => {
            const on = i === index;
            if (on === card.classList.contains("is-active")) return;
            card.classList.toggle("is-active", on);
          });
        };

        this.hotspotPins.forEach((pin, index) => {
          const handler = () => setActive(index);
          pin.addEventListener("click", handler);
          this.hotspotHandlers.push({ pin, handler });
        });

        setActive(0);
        this.mobileHotspotsReady = true;
        return true;
      }

      teardownMobileHotspots() {
        if (this.hotspotHandlers) {
          this.hotspotHandlers.forEach(({ pin, handler }) => {
            pin.removeEventListener("click", handler);
          });
        }
        this.hotspotHandlers = null;
        this.hotspotPins = null;
        this.hotspotCards = null;
        this.hotspotIndex = null;
        this.mobileHotspotsReady = false;
      }

      setupMobileCarousel() {
        if (!this.mediaQuery?.matches || !this.isFeaturedBanner) return;

        this.teardownMobileCarousel();

        const scrollEl = this.hasAttribute("data-featured-banner-scroll")
          ? this
          : this.querySelector("[data-featured-banner-scroll]");
        const dotsContainer = this.closest(
          ".featured-banner__mobile-carousel",
        )?.querySelector("[data-featured-banner-dots]");

        if (!scrollEl || !dotsContainer) return;

        this.scrollEl = scrollEl;
        this.dots = dotsContainer.querySelectorAll(".featured-banner__dot");

        this.updateMobileDot = () => {
          const slideWidth = scrollEl.clientWidth;
          if (!slideWidth || !this.dots?.length) return;
          const index = Math.max(
            0,
            Math.min(
              this.dots.length - 1,
              Math.round(scrollEl.scrollLeft / slideWidth),
            ),
          );
          this.dots.forEach((dot, i) => {
            const active = i === index;
            dot.classList.toggle("is-active", active);
            dot.setAttribute("aria-selected", active ? "true" : "false");
          });
        };

        this.onMobileScroll = this.safeThrottle(
          () => this.updateMobileDot(),
          80,
        );
        scrollEl.addEventListener("scroll", this.onMobileScroll, {
          passive: true,
        });

        this.dotHandlers = [];
        this.dots.forEach((dot, index) => {
          const handler = () => {
            scrollEl.scrollTo({
              left: index * scrollEl.clientWidth,
              behavior: "smooth",
            });
          };
          dot.addEventListener("click", handler);
          this.dotHandlers.push({ dot, handler });
        });

        this.onMobileResize = this.safeThrottle(
          () => this.updateMobileDot(),
          100,
        );
        window.addEventListener("resize", this.onMobileResize);

        this.updateMobileDot();
        this.mobileCarouselReady = true;
      }

      teardownMobileCarousel() {
        if (this.scrollEl && this.onMobileScroll) {
          this.scrollEl.removeEventListener("scroll", this.onMobileScroll);
        }
        if (this.dotHandlers) {
          this.dotHandlers.forEach(({ dot, handler }) => {
            dot.removeEventListener("click", handler);
          });
        }
        if (this.onMobileResize) {
          window.removeEventListener("resize", this.onMobileResize);
        }
        this.mobileCarouselReady = false;
        this.scrollEl = null;
        this.dots = null;
        this.onMobileScroll = null;
        this.onMobileResize = null;
        this.dotHandlers = null;
        this.updateMobileDot = null;
      }

      setupInitialStates() {
        this.infoElements.forEach((element) => {
          element.style.opacity = "0";
          if (typeof window.Motion === "undefined") {
            element.style.transition =
              "opacity 0.8s ease-out, transform 0.8s cubic-bezier(0.17, 0.67, 0.3, 1.33)";
          }
        });
      }

      setupFeaturedBanner() {
        if (this.revealTimer) {
          window.clearTimeout(this.revealTimer);
          this.revealTimer = null;
        }
        this.classList.remove("is-ready", "is-animating");
        this.setAttribute("data-banner-pending", "");
        this.infoElements.forEach((element) => {
          element.style.opacity = "";
          element.style.transform = "";
          element.style.willChange = "opacity, transform";
        });

        const play = () => {
          if (this.isVisible) return;
          this.isVisible = true;
          if (this.safetyTimer) {
            window.clearTimeout(this.safetyTimer);
            this.safetyTimer = null;
          }
          this.playFeaturedBannerAnimation();
          if (this.observer) {
            this.observer.disconnect();
            this.observer = null;
          }
        };

        if (typeof IntersectionObserver === "undefined") {
          play();
          return;
        }

        const section =
          this.closest(".section-featured-product-banner") || this;

        const observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              play();
            });
          },
          {
            root: null,
            rootMargin: "0px 0px -10% 0px",
            threshold: 0.15,
          },
        );

        observer.observe(section);
        this.observer = observer;

        // Only auto-play if already well into view (do not fire instantly on load).
        requestAnimationFrame(() => {
          const rect = section.getBoundingClientRect();
          const view = window.innerHeight || 1;
          if (rect.top < view * 0.55 && rect.bottom > view * 0.25) play();
        });

        this.safetyTimer = window.setTimeout(() => play(), 4000);
      }

      playFeaturedBannerAnimation() {
        const reduceMotion = window.matchMedia(
          "(prefers-reduced-motion: reduce)",
        ).matches;

        const finish = () => {
          this.removeAttribute("data-banner-pending");
          this.classList.add("is-ready");
          this.classList.remove("is-animating");
          this.infoElements.forEach((element) => {
            element.style.opacity = "1";
            // Clear inline transform so permanent CSS nudges (Cognitive / Easy) apply.
            element.style.transform = "";
            element.style.willChange = "auto";
            const card = element.querySelector(
              ".featured-banner-card, .block_infor-text",
            );
            if (card) {
              card.style.opacity = "1";
              card.style.visibility = "visible";
              card.style.transform = "";
            }
          });
        };

        if (reduceMotion) {
          finish();
          return;
        }

        // Force a reflow so the pending off-screen state is painted first.
        // content-effect uses display:contents, so reflow a child card instead.
        this.classList.add("is-animating");
        const firstCard = this.infoElements[0];
        if (firstCard) void firstCard.offsetWidth;
        this.classList.add("is-ready");

        if (this.revealTimer) window.clearTimeout(this.revealTimer);
        const sectionRoot =
          this.closest(".featured-banner--clouds") ||
          this.closest(".section-featured-product-banner")?.querySelector(
            ".featured-banner--clouds",
          );
        const revealMs = sectionRoot ? 2000 : 1200;
        this.revealTimer = window.setTimeout(finish, revealMs);
      }

      setupScrollObserver() {
        const play = () => {
          if (this.isVisible) return;
          this.isVisible = true;
          if (this.safetyTimer) {
            window.clearTimeout(this.safetyTimer);
            this.safetyTimer = null;
          }
          this.playEntranceAnimation();
          if (this.observer) {
            this.observer.disconnect();
            this.observer = null;
          }
        };

        const options = {
          root: null,
          rootMargin: "0px",
          threshold: 0.05,
        };

        const observer = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            play();
          });
        }, options);
        this.observer = observer;
        observer.observe(this);
        this.safetyTimer = window.setTimeout(play, 1200);
      }

      playEntranceAnimation() {
        const reduceMotion = window.matchMedia(
          "(prefers-reduced-motion: reduce)",
        ).matches;
        const motion = window.Motion;
        const showCards = () => {
          this.infoElements.forEach((element) => {
            element.style.opacity = "1";
            element.style.transform = "translateX(0)";
          });
        };

        if (reduceMotion || typeof motion?.animate !== "function") {
          showCards();
          this.classList.add("is-ready");
          return;
        }

        try {
          const isRTL = document.documentElement.dir === "rtl";

          this.infoElements.forEach((element, index) => {
            const pairIndex = Math.floor(index / 2);
            const delay = pairIndex * 0.1;
            const isEven = index % 2 === 0;
            const startX = isEven ? (isRTL ? 40 : -40) : isRTL ? -40 : 40;

            motion.animate(
              element,
              {
                opacity: [0, 1],
                x: [startX, 0],
              },
              {
                delay,
                duration: 0.5,
                easing: [0, 0, 0.3, 1],
                onComplete: () => {
                  element.style.opacity = "1";
                  element.style.transform = "translateX(0)";
                },
              },
            );
          });
        } catch (_error) {
          showCards();
        }

        this.classList.add("is-ready");
        if (this.revealTimer) window.clearTimeout(this.revealTimer);
        this.revealTimer = window.setTimeout(showCards, 1200);
      }

      disconnectedCallback() {
        if (this.safetyTimer) {
          window.clearTimeout(this.safetyTimer);
          this.safetyTimer = null;
        }
        if (this.revealTimer) {
          window.clearTimeout(this.revealTimer);
          this.revealTimer = null;
        }
        this.initialized = false;
        this.isVisible = false;
        this.teardownMobileCarousel();
        this.teardownMobileHotspots();
        if (this.observer) {
          this.observer.disconnect();
          this.observer = null;
        }
        if (this.mediaQuery && this.handleMediaQueryChange) {
          this.mediaQuery.removeEventListener(
            "change",
            this.handleMediaQueryChange,
          );
        }
      }
    },
  );
}
